/* Amranthé nad-cream product page: gallery, offer picker, delivery date, sticky bar. */

if (!customElements.get('am-gallery')) {
  customElements.define(
    'am-gallery',
    class AmGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.thumbs = [...this.querySelectorAll('[data-thumb]')];
        if (!this.track || this.slides.length === 0) return;

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb)))
        );
        this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
          if (e.key === 'ArrowRight') this.goTo(this.index + 1);
        });

        this.index = 0;
        this.track.addEventListener('scroll', () => {
          window.cancelAnimationFrame(this.frame);
          this.frame = window.requestAnimationFrame(() => this.syncFromScroll());
        });
        this.toggleAttribute('data-single', this.slides.length < 2);
      }

      goTo(index) {
        const count = this.slides.length;
        const target = (index + count) % count;
        this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: 'smooth' });
        this.setActive(target);
      }

      syncFromScroll() {
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (index !== this.index) this.setActive(index);
      }

      setActive(index) {
        this.index = index;
        this.thumbs.forEach((thumb, i) => {
          const active = i === index;
          thumb.classList.toggle('is-active', active);
          if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        });
        this.slides.forEach((slide, i) => {
          if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
        });
      }
    }
  );
}

if (!customElements.get('am-offer')) {
  customElements.define(
    'am-offer',
    class AmOffer extends HTMLElement {
      connectedCallback() {
        this.variant = this.querySelector('[data-variant-select]'); // <select name="id"> or hidden input
        this.planInput = this.querySelector('[data-offer-plan]');
        this.priceCurrent = this.querySelector('[data-price-current]');
        this.priceCompare = this.querySelector('[data-price-compare]');
        this.qtyInput = this.querySelector('[data-qty-input]');
        this.buytype = [...this.querySelectorAll('input[name^="am-buytype"]')];

        this.variant?.addEventListener('change', () => this.update());
        this.buytype.forEach((r) => r.addEventListener('change', () => this.update()));

        // quantity stepper
        this.querySelectorAll('[data-qty-step]').forEach((btn) =>
          btn.addEventListener('click', () => this.stepQty(Number(btn.dataset.qtyStep)))
        );
        this.qtyInput?.addEventListener('input', () => {
          const n = parseInt(this.qtyInput.value, 10);
          this.qtyInput.value = Number.isFinite(n) && n > 0 ? n : '';
        });
        this.qtyInput?.addEventListener('blur', () => {
          if (!(parseInt(this.qtyInput.value, 10) > 0)) this.qtyInput.value = 1;
        });

        this.update();
      }

      stepQty(delta) {
        const current = parseInt(this.qtyInput?.value, 10) || 1;
        if (this.qtyInput) this.qtyInput.value = Math.max(1, current + delta);
      }

      // the currently-chosen variant <option> (or the hidden single-variant input)
      get variantEl() {
        if (this.variant && this.variant.tagName === 'SELECT') {
          return this.variant.options[this.variant.selectedIndex];
        }
        return this.variant; // hidden input carries data-price
      }

      update() {
        const sub = this.buytype.find((r) => r.checked && r.value === 'subscribe');
        this.buytype.forEach((r) => r.closest('.am-buytype__opt')?.classList.toggle('is-selected', r.checked));

        // selling plan (subscribe) vs one-time
        const planId = sub ? sub.dataset.planId : '';
        if (this.planInput) {
          this.planInput.value = planId || '';
          this.planInput.disabled = !planId;
        }

        // price display: subscribe price, else the selected variant's price
        const el = this.variantEl;
        const unit = sub ? sub.dataset.price : el?.dataset.price;
        const compare = sub ? '' : el?.dataset.compare;
        if (this.priceCurrent && unit) this.priceCurrent.textContent = unit;
        if (this.priceCompare) {
          if (compare) {
            this.priceCompare.textContent = compare;
            this.priceCompare.hidden = false;
          } else {
            this.priceCompare.hidden = true;
          }
        }

        this.price = unit || '';
        this.dispatchEvent(new CustomEvent('am:offer-change', { bubbles: true, detail: { price: this.price } }));
      }
    }
  );
}

if (!customElements.get('am-delivery')) {
  customElements.define(
    'am-delivery',
    class AmDelivery extends HTMLElement {
      connectedCallback() {
        const out = this.querySelector('[data-delivery-date]');
        if (!out) return;
        const now = new Date();
        const start = new Date(now);
        // After the cutoff hour (or on a weekend) the parcel leaves the next business day.
        if (now.getHours() >= Number(this.dataset.cutoff || 14)) start.setDate(start.getDate() + 1);
        const min = this.addBusinessDays(start, Number(this.dataset.min || 2));
        const max = this.addBusinessDays(start, Number(this.dataset.max || 4));
        const locale = document.documentElement.lang || undefined;
        const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
        out.textContent = min.getTime() === max.getTime() ? fmt.format(min) : `${fmt.format(min)} – ${fmt.format(max)}`;
      }

      addBusinessDays(from, days) {
        const date = new Date(from);
        while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          if (date.getDay() !== 0 && date.getDay() !== 6) added++;
        }
        return date;
      }
    }
  );
}

if (!customElements.get('am-sticky')) {
  customElements.define(
    'am-sticky',
    class AmSticky extends HTMLElement {
      connectedCallback() {
        this.offer = document.getElementById(this.dataset.target);
        if (!this.offer) return;
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.button = this.querySelector('[data-sticky-submit]');

        const sync = () => {
          if (this.priceEl && this.offer.price) this.priceEl.textContent = this.offer.price;
        };
        this.offer.addEventListener('am:offer-change', sync);
        sync();

        this.button.addEventListener('click', () => {
          const form = this.offer.querySelector('form');
          const submit = form?.querySelector('[type="submit"]');
          if (!form || submit?.disabled) return;
          form.requestSubmit(submit);
        });

        // Visible whenever the real add-to-cart button is off-screen: below the fold on arrival
        // (phones) or scrolled past. A scroll check (not an IntersectionObserver) so jumps past
        // the offer (flings, anchors, restored scroll) still count.
        const atc = this.offer.querySelector('[type="submit"]') || this.offer;
        let frame;
        const check = () => {
          const r = atc.getBoundingClientRect();
          const show = r.bottom < 0 || r.top > window.innerHeight - this.offsetHeight;
          if (show === this.classList.contains('is-visible')) return;
          this.classList.toggle('is-visible', show);
          document.body.classList.toggle('am-sticky-on', show);
          this.setAttribute('aria-hidden', String(!show));
          this.button.tabIndex = show ? 0 : -1;
        };
        window.addEventListener(
          'scroll',
          () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(check);
          },
          { passive: true }
        );
        window.addEventListener('resize', check, { passive: true });
        check();
      }
    }
  );
}

/* Scroll-to-offer links (e.g. guarantee CTA with href="#buy"). */
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href="#buy"]');
  if (!link) return;
  const offer = document.querySelector('am-offer');
  if (!offer) return;
  event.preventDefault();
  offer.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

/* Ingredient tabs (v2): Key Cosmeceuticals / Botanicals / All ingredients. */
if (!customElements.get('am-tabs')) {
  customElements.define(
    'am-tabs',
    class AmTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-tab]')];
        this.panels = [...this.querySelectorAll('[data-panel]')];
        if (!this.tabs.length) return;
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.show(Number(tab.dataset.tab)));
          tab.addEventListener('keydown', (e) => {
            const i = Number(tab.dataset.tab);
            if (e.key === 'ArrowRight') { e.preventDefault(); this.focusTab(i + 1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); this.focusTab(i - 1); }
          });
        });
      }
      focusTab(i) {
        const count = this.tabs.length;
        const target = (i + count) % count;
        this.tabs[target].focus();
        this.show(target);
      }
      show(index) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        this.panels.forEach((panel, i) => {
          const active = i === index;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
      }
    }
  );
}

/* Share row (v2): copy-link button. */
if (!customElements.get('am-share')) {
  customElements.define(
    'am-share',
    class AmShare extends HTMLElement {
      connectedCallback() {
        const btn = this.querySelector('[data-share-copy]');
        if (!btn) return;
        const note = btn.querySelector('[data-share-copied]');
        btn.addEventListener('click', async () => {
          const url = this.dataset.url || location.href;
          try {
            await navigator.clipboard.writeText(url);
          } catch (e) {
            const t = document.createElement('textarea');
            t.value = url; document.body.appendChild(t); t.select();
            try { document.execCommand('copy'); } catch (err) {}
            t.remove();
          }
          if (note) {
            note.hidden = false;
            clearTimeout(this.timer);
            this.timer = setTimeout(() => { note.hidden = true; }, 1600);
          }
        });
      }
    }
  );
}
